import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'

import { type ClientUiPublic } from '../../api/platformConfig'
import { normalizeCharacterCards } from '../../lib/comicCharacters'
import {
  type ComicProjectCreative,
  normalizeComicProjectCreative,
  validateComicProjectCreative,
  warningsForCreativeChange,
} from '../../lib/comicCreativeBible'
import type { ComicPageReferenceCapContext } from '../../lib/comicPageReferenceCap'
import {
  type ComicProjectSettings,
  normalizeComicProjectSettings,
  projectHasGeneratedImages,
  syncComicProjectSettingsToPanelAspect,
  validateComicProjectSettingsForPanelAspect,
} from '../../lib/comicProjectSettings'
import type { ComicProject } from '../../lib/comicTypes'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ThemeLayerCrossfade } from '../motion/ThemeMotion'
import { ThemeModal } from '../modal'
import { ThemeLabeledSegment } from '../theme/ThemeLabeledSegment'
import { ComicCharacterCardsEditor } from './ComicCharacterCardsEditor'
import { ComicCreativeSettingsForm } from './ComicCreativeSettingsForm'
import { comicPanelRoundSm } from './comicPanelTokens'
import { ComicProjectSettingsForm } from './ComicProjectSettingsForm'

type SettingsTab = 'basic' | 'generation' | 'characters'

const SETTINGS_TABS = [
  { value: 'basic' as const, label: '基础配置' },
  { value: 'generation' as const, label: '生图配置' },
  { value: 'characters' as const, label: '角色卡' },
]

/** 选项卡内容区固定高度，切换时弹窗不抖动 */
const TAB_PANEL_HEIGHT_CLASS = 'min-h-[min(52vh,480px)]'

type Props = {
  open: boolean
  angular: boolean
  project: ComicProject
  clientUi: ClientUiPublic
  saving: boolean
  onClose: () => void
  onSave: (creative: ComicProjectCreative, settings: ComicProjectSettings | null) => void
  referenceCap?: ComicPageReferenceCapContext | null
}

function TabPanelScroll({ children }: { children: ReactNode }) {
  return (
    <div className="scroll-region h-full min-h-0 overflow-y-auto overscroll-y-contain pe-0.5">
      {children}
    </div>
  )
}

export function ComicCreativeSettingsModal({
  open,
  angular,
  project,
  clientUi,
  saving,
  onClose,
  onSave,
  referenceCap = null,
}: Props) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('basic')
  const [creativeDraft, setCreativeDraft] = useState(project.creative)
  const [settingsDraft, setSettingsDraft] = useState<ComicProjectSettings | null>(null)

  useEffect(() => {
    if (open) {
      setActiveTab('basic')
      setCreativeDraft(project.creative)
      setSettingsDraft(
        validateComicProjectCreative(project.creative).ok
          ? syncComicProjectSettingsToPanelAspect(project.settings, clientUi, project.creative.panelAspect)
          : project.settings,
      )
    }
  }, [open, project, clientUi])

  const hasImages = projectHasGeneratedImages(project)
  const warnings = warningsForCreativeChange(project.creative, creativeDraft, hasImages)
  const creativeValidation = validateComicProjectCreative(creativeDraft)
  const settingsValidation =
    settingsDraft && creativeValidation.ok
      ? validateComicProjectSettingsForPanelAspect(settingsDraft, clientUi, creativeDraft.panelAspect)
      : { ok: true as const }

  const handleCreativeChange = (next: ComicProjectCreative) => {
    const normalized = normalizeComicProjectCreative(next)
    setCreativeDraft(normalized)
    setSettingsDraft((prev) =>
      prev && validateComicProjectCreative(normalized).ok
        ? syncComicProjectSettingsToPanelAspect(prev, clientUi, normalized.panelAspect)
        : prev,
    )
  }

  const handleSettingsChange = (next: ComicProjectSettings) => {
    setSettingsDraft(normalizeComicProjectSettings(next, clientUi))
  }

  const handleSave = () => {
    const characters = normalizeCharacterCards(creativeDraft.characters)
    const normalizedCreative = normalizeComicProjectCreative({
      ...creativeDraft,
      characters,
      characterNotes: '',
      characterReferenceUrls: [],
    })
    const validation = validateComicProjectCreative(normalizedCreative)
    if (!validation.ok) return
    if (settingsDraft) {
      const settingsCheck = validateComicProjectSettingsForPanelAspect(
        settingsDraft,
        clientUi,
        normalizedCreative.panelAspect,
      )
      if (!settingsCheck.ok) return
    }
    onSave(
      normalizedCreative,
      settingsDraft ? normalizeComicProjectSettings(settingsDraft, clientUi) : null,
    )
  }

  const tabLayerStyle = (tab: SettingsTab): CSSProperties => ({
    pointerEvents: activeTab === tab ? 'auto' : 'none',
    zIndex: activeTab === tab ? 2 : 1,
  })

  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title="画册设置"
      angular={angular}
      size="lg"
      panelMinHeightClass="min-h-[min(72vh,580px)]"
      bodyScrollable={false}
      bodyClassName="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-5 sm:py-5"
      footer={
        <div className="flex justify-end gap-2">
          <ChromeToolbarButton type="button" onClick={onClose} disabled={saving}>
            取消
          </ChromeToolbarButton>
          <ChromePrimaryButton
            density="compact"
            type="button"
            onClick={handleSave}
            disabled={saving || !creativeValidation.ok || !settingsValidation.ok}
          >
            {saving ? '保存中…' : '保存'}
          </ChromePrimaryButton>
        </div>
      }
    >
      {warnings.length > 0 ? (
        <ul
          className={[
            'mb-4 shrink-0 space-y-2 border border-[color-mix(in_srgb,var(--line)_60%,var(--color-warning)_40%)] bg-[color-mix(in_srgb,var(--panel)_92%,var(--color-warning)_8%)] px-3 py-2.5 text-xs leading-relaxed text-ink',
            comicPanelRoundSm(angular),
          ].join(' ')}
          role="status"
        >
          {warnings.map((w) => (
            <li key={w.field}>· {w.message}</li>
          ))}
        </ul>
      ) : null}
      <ThemeLabeledSegment
        angular={angular}
        borderless
        value={activeTab}
        options={SETTINGS_TABS}
        onChange={setActiveTab}
        aria-label="画册设置分区"
        className="mb-4 flex w-full shrink-0 [&>button]:min-w-0 [&>button]:flex-1"
      />
      <div
        className={['relative grid min-h-0 flex-1 grid-cols-1 grid-rows-1', TAB_PANEL_HEIGHT_CLASS].join(' ')}
        role="tabpanel"
        aria-label={SETTINGS_TABS.find((t) => t.value === activeTab)?.label}
      >
        <ThemeLayerCrossfade
          className="col-start-1 row-start-1 flex min-h-0 flex-col overflow-hidden"
          visible={activeTab === 'basic'}
          fadeOnly
          style={tabLayerStyle('basic')}
        >
          <TabPanelScroll>
            <ComicCreativeSettingsForm
              angular={angular}
              clientUi={clientUi}
              modelId={settingsDraft?.modelId ?? project.settings.modelId}
              platformId={settingsDraft?.platformId ?? project.settings.platformId}
              value={creativeDraft}
              onChange={handleCreativeChange}
            />
          </TabPanelScroll>
        </ThemeLayerCrossfade>
        <ThemeLayerCrossfade
          className="col-start-1 row-start-1 flex min-h-0 flex-col overflow-hidden"
          visible={activeTab === 'generation'}
          fadeOnly
          style={tabLayerStyle('generation')}
        >
          <TabPanelScroll>
            <div className="space-y-5">
              {settingsDraft && !settingsValidation.ok ? (
                <p className="text-sm text-[color-mix(in_srgb,var(--ink)_75%,var(--color-warning)_25%)]" role="alert">
                  {settingsValidation.message}
                </p>
              ) : null}
              <ComicProjectSettingsForm
                angular={angular}
                clientUi={clientUi}
                panelAspect={creativeDraft.panelAspect}
                value={settingsDraft ?? project.settings}
                onChange={handleSettingsChange}
                generationOnly
              />
            </div>
          </TabPanelScroll>
        </ThemeLayerCrossfade>
        <ThemeLayerCrossfade
          className="col-start-1 row-start-1 flex min-h-0 flex-col overflow-hidden"
          visible={activeTab === 'characters'}
          fadeOnly
          style={tabLayerStyle('characters')}
        >
          <TabPanelScroll>
            <ComicCharacterCardsEditor
              angular={angular}
              value={creativeDraft.characters}
              onChange={(characters) => setCreativeDraft((prev) => ({ ...prev, characters }))}
              referenceCap={referenceCap}
            />
          </TabPanelScroll>
        </ThemeLayerCrossfade>
      </div>
    </ThemeModal>
  )
}
